import {
  REACT_APP_COUNTRY,
  REACT_APP_ENVIRONMENT,
  REACT_APP_INTAKE_CHAT,
  REACT_APP_INTAKE_SCHEMA_PATH,
} from '../config';
import { getRuntimeFlag } from './runtimeFlags';

export type IntakePathFlag = 'on' | 'off';

/**
 * Estado de una feature de intake. `off` cierra entradas nuevas pero deja que las
 * sesiones ya iniciadas terminen su flujo (semántica histórica del flag); `kill` es el
 * apagado duro que además ignora esas sesiones — ver `resolveIntakePath`.
 */
export type IntakeFeatureMode = 'on' | 'off' | 'kill';

/** Environments where ?intake=0|1 is honored (QUOTE-HANDOFF §8). */
const URL_OVERRIDE_ALLOWLIST = new Set(['development', 'staging', 'stg', 'dev']);

/**
 * Estado efectivo del path schema-driven: manda el override de runtime
 * (`flags.json`) y, si no hay, la env var compilada del build. Sin `flags.json` el
 * comportamiento es exactamente el histórico.
 */
export function getIntakeSchemaPathMode(): IntakeFeatureMode {
  return getRuntimeFlag('intakeSchemaPath') ?? normalizeFlag(REACT_APP_INTAKE_SCHEMA_PATH);
}

/** Ídem para el chatbot (CHATBOT.md Paso 3). */
export function getIntakeChatMode(): IntakeFeatureMode {
  return getRuntimeFlag('intakeChat') ?? normalizeFlag(REACT_APP_INTAKE_CHAT);
}

export function isIntakeSchemaPathEnabled(): boolean {
  return getIntakeSchemaPathMode() === 'on';
}

/**
 * Kill switch del intake nuevo: apaga el form **y** el chat (el chat es una
 * presentación del mismo path schema-driven). Para apagar solo el chat existe
 * `isIntakeChatKilled`.
 */
export function isIntakeSchemaPathKilled(): boolean {
  return getIntakeSchemaPathMode() === 'kill';
}

/**
 * Chatbot flag for this market build (CHATBOT.md Paso 3). Off → `?channel=chat` is inert.
 *
 * Matar el path schema-driven también apaga el chat: el chat ES ese path, solo que con otra
 * presentación. Sin esto el CTA "Iniciar por chat" seguiría visible en la cotización y
 * llevaría derecho a la pantalla de "no disponible".
 */
export function isIntakeChatEnabled(): boolean {
  return getIntakeChatMode() === 'on' && !isIntakeSchemaPathKilled();
}

/** Kill del chat: propio, o heredado del kill del path schema-driven que lo contiene. */
export function isIntakeChatKilled(): boolean {
  return getIntakeChatMode() === 'kill' || isIntakeSchemaPathKilled();
}


/**
 * True when the URL asks to start this application by chat (`?channel=chat`).
 *
 * Unlike `?intake=`, this is a production entry point (the quote screen links here),
 * so it is not restricted to the dev/stg allowlist — the market flag is the only gate.
 * It is a first-entry hint: an existing session's `channel` always wins (see
 * `resolveIntakeExperience`).
 */
export function isIntakeChatRequested(
  search: string = typeof window !== 'undefined' ? window.location.search : '',
  chatEnabled: boolean = isIntakeChatEnabled(),
): boolean {
  if (!chatEnabled) return false;
  const params = new URLSearchParams(search.startsWith('?') ? search.slice(1) : search);
  return params.get('channel')?.trim().toLowerCase() === 'chat';
}

export function isIntakeUrlOverrideAllowed(environment: string = REACT_APP_ENVIRONMENT): boolean {
  return URL_OVERRIDE_ALLOWLIST.has(String(environment).toLowerCase().trim());
}

/**
 * Dev/stg-only UI chrome for intake (path badge, etc.). Never in production.
 * Same allowlist as URL overrides (`development` | `dev` | `staging` | `stg`).
 */
export function isIntakeDevChromeVisible(environment: string = REACT_APP_ENVIRONMENT): boolean {
  return isIntakeUrlOverrideAllowed(environment);
}

/**
 * True when /application may render without a legacy quote_form (W5a new path).
 * Used by QuoteFormSummaryProvider so it does not bounce found:false → /quote
 * before/while ApplicationPathGate mounts IntakeWizard or the chat.
 */
export function allowsApplicationWithoutLegacyQuoteForm(
  search: string = typeof window !== 'undefined' ? window.location.search : '',
  chatEnabled: boolean = isIntakeChatEnabled(),
): boolean {
  // Va primero: con el intake nuevo matado el provider legacy vuelve a ser el que rebota a
  // /quote cuando no hay solicitud. Debajo de `chatEnabled` esta guarda era inalcanzable justo
  // en los mercados con chat, que son los que más probablemente se maten.
  if (isIntakeSchemaPathKilled()) return false;
  if (isIntakeSchemaPathEnabled()) return true;
  // In a chat-enabled market any /application hit may be a chat session (which has no
  // legacy quote_form), and only the async probe can tell — including on resume, when
  // the URL carries no `channel` param. Bouncing here would kill that resume, so the
  // gate becomes authoritative: it re-issues the /quote bounce itself once it knows
  // there is neither a session nor a legacy application.
  if (chatEnabled) return true;
  if (isIntakeChatRequested(search, chatEnabled)) return true;
  if (!isIntakeUrlOverrideAllowed()) return false;
  const params = new URLSearchParams(search.startsWith('?') ? search.slice(1) : search);
  const raw = params.get('intake');
  return raw === '1' || raw === 'true';
}

export function currentDeployMarket(): string {
  return String(REACT_APP_COUNTRY || 'MX').toUpperCase().trim();
}

/**
 * QA-only pin for createIntakeSession (`?schemaVersion=1.2.0`).
 * Ignored outside the URL override allowlist (same as `?intake=`).
 */
export function resolveQaSchemaVersionOverride(
  search: string = typeof window !== 'undefined' ? window.location.search : '',
  urlOverrideAllowed: boolean = isIntakeUrlOverrideAllowed(),
): string | undefined {
  if (!urlOverrideAllowed) return undefined;
  const params = new URLSearchParams(search.startsWith('?') ? search.slice(1) : search);
  const raw = params.get('schemaVersion')?.trim();
  if (!raw) return undefined;
  return raw;
}

function normalizeFlag(raw: string | undefined): IntakePathFlag {
  const v = String(raw || 'off').toLowerCase().trim();
  return v === 'on' || v === 'true' || v === '1' ? 'on' : 'off';
}
